/**
 * Utility Partner Manager
 * Manages utility partner selection and displays dynamic content across the theme
 *
 * Features:
 * - Load utility providers from metaobjects
 * - Manage partner selection via localStorage
 * - Calculate utility-specific discounts for products from rebate programs
 * - Normalize product handles (removes -legacy suffix) for consistent lookups
 *   Both 'mysa-baseboard' and 'mysa-baseboard-legacy' will match the same discount
 * - Set cart attributes for rebate prequalification
 * - Auto-calculate post-tax rebate amounts for specific providers
 * - Apply price floors to ensure minimum product prices
 * - Read discount configuration from variant metafields (price floors, Mysa discounts, BYOB inclusion)
 *
 * Cart Attributes:
 * - _utility_partner_code: utility_provider metaobject handle (e.g., "efficiencyone") — canonical
 *   order-tag identifier; resolves via `mysa content metaobject list --type utility_provider`
 * - _utility_program_code: utility_program family handle when the wizard selects a specific program
 *   (required for multi-program providers such as puget-sound-energy); scopes order tags to that program
 * - _utility_offer_mode: resolved offer on the selected program (`instant_checkout`, `post_purchase`,
 *   `marketplace_only`); mirrors utility_program.mysa_offer_mode for app tagging without line iteration
 * - _rebate_prequalify: "1" when a utility partner is selected
 *
 * Theme pricing only applies when `appliesOffersCheckoutFloors()` is true
 * and the variant has a matching `$app:offers` utility-incentives entry (see
 * `selectionAllowsInstantPricing()`, `utility-instant-pricing-eligible.liquid`,
 * and `#hasConfiguredUtilityIncentive()`).
 *
 * Rebate Calculation:
 * - Instant-checkout amounts, price floors, and stacking flags come from
 *   `$app:offers` utility-incentives on the variant (same source as Liquid SSR)
 * - Provider identity is the utility_provider metaobject handle (not legacy ca-/us- codes)
 *
 * Integration with discount-manager.js:
 * The DiscountManager can call:
 * - getUtilityDiscount(productHandle) - Get total discount amount in cents
 * - getUtilityDiscountDetails(productHandle, quantity) - Get full discount info
 * - getUtilityProgramBreakdown(productHandle, quantity) - Get rebate + mysa discount separate
 * - isProductEligible(productHandle) - Check if product has utility discount
 *
 * Example usage in discount-manager.js:
 * ```js
 * import { getUtilityPartnerManager } from '@theme/utility-partner-manager';
 *
 * // Get total discount
 * const utilityManager = getUtilityPartnerManager();
 * const utilityDiscount = utilityManager.getUtilityDiscount(productHandle);
 * if (utilityDiscount > 0) {
 *   this.currentDiscounts.utility = utilityDiscount * quantity;
 *   this.currentDiscounts.total += this.currentDiscounts.utility;
 * }
 *
 * // Get program breakdown for display (rebate + mysa discount separate, quantity-aware)
 * const breakdown = utilityManager.getUtilityProgramBreakdown(productHandle, quantity);
 * // Returns: {
 * //   rebate: 3913,              // Total rebate in cents (quantity-adjusted)
 * //   mysaDiscount: 1400,        // Total Mysa discount in cents (quantity-adjusted)
 * //   total: 5313,               // Combined total in cents
 * //   providerName: 'Saint John Energy',
 * //   priceFloor: 4587,          // cents, null if no floor
 * //   rebateQuantityLimit: 5,    // 0 = no limit
 * //   mysaDiscountQuantityLimit: 0,
 * //   includeBYOB: true,         // Whether BYOB discount can stack
 * //   maxQuantity: 0,            // Max quantity for rebate (0 = no limit)
 * //   programs: [
 * //     { type: 'rebate', label: '...', amount: 3913, perUnit: 3913, quantityApplied: 1 },
 * //     { type: 'mysa-discount', label: '...', amount: 1400, perUnit: 1400, quantityApplied: 1 }
 * //   ]
 * // }
 * ```
 *
 * @see UTILITY_MANAGER.md for full documentation and roadmap
 */

import { createLogger, createReporter } from "@theme/logger";
import {
  UtilityPartnerLoadedEvent,
  UtilityPartnerChangedEvent,
  UtilityPartnerClearedEvent,
  UtilityPostalUpdatedEvent,
} from "@theme/mysa-events";
import {
  getCountryFromPostalCode,
  UtilityStorage,
} from "@theme/utility-storage";
// Pure helpers + constants live in `utility-shared.js` so the pricing path
// (buy-box, price-breakdown, discount-manager) can import them WITHOUT pulling
// in this heavy module. Imported here for internal use and re-exported below so
// existing `@theme/utility-partner-manager` imports keep working.
import {
  UTILITY_BUNDLE_TEMPLATE,
  UTILITY_OFFER_MODE,
  canonicalUtilityHandle,
  isOntarioHrsHandle,
  isHiloHandle,
  appliesOffersCheckoutFloors,
  isUtilityBundleTemplate,
  programCollectionPath,
} from "@theme/utility-shared";

const log = createLogger("UtilityPartnerManager");
const reportError = createReporter("UtilityPartnerManager");

/**
 * @typedef {Object} CountryData
 * @property {string} [code] - Country code
 * @property {string} [iso] - ISO country code
 */

/**
 * @typedef {Object} ProvinceData
 * @property {string} [code] - Province/state code
 * @property {string} [iso] - ISO province code
 */

/**
 * @typedef {Object} InstantRebateOverride
 * @property {boolean} enabled - Whether the instant rebate is enabled
 * @property {number} rebate_amount - Rebate amount in dollars
 * @property {number|null} price_floor - Minimum price floor in dollars, null if no floor
 * @property {number|null} [rebate_override] - Override rebate amount or multiplier
 * @property {number} [max_quantity] - Maximum quantity eligible for rebate (0 = no limit)
 * @property {boolean} [include_byob] - Whether BYOB discount can stack with this rebate
 * @property {boolean} [include_general] - Whether general discount can stack with this rebate
 */

/**
 * @typedef {Object} DemandResponseOverride
 * @property {boolean} enabled - Whether demand response is enabled
 * @property {number} discount_amount - Discount amount in dollars
 */

/**
 * @typedef {Object} MysaDiscountOverride
 * @property {boolean} enabled - Whether Mysa discount is enabled
 * @property {number} discount_override - Discount amount in dollars
 * @property {number} [quantity_limit] - Maximum quantity eligible for discount (0 = no limit)
 */

/**
 * @typedef {Object} UtilityIncentiveOverride
 * @property {InstantRebateOverride} [instant_rebate] - Instant rebate configuration
 * @property {DemandResponseOverride} [demand_response] - Demand response configuration
 * @property {MysaDiscountOverride} [mysa_discount] - Mysa discount configuration
 */

/**
 * @typedef {Object} DiscountProgram
 * @property {number} rebate - Rebate amount in cents
 * @property {number} mysaDiscount - Mysa discount amount in cents
 * @property {number} total - Total discount amount in cents (rebate + mysaDiscount)
 * @property {string} providerName - Display name of the utility provider
 * @property {number|null} priceFloor - Minimum price in cents (prevents stacking other discounts), null if no floor
 * @property {boolean} [includeBYOB] - Whether BYOB discount can stack with this utility program
 * @property {boolean} [includeGeneral] - Whether general discount can stack with this utility program
 * @property {number} [maxQuantity] - Maximum quantity eligible for this program (0 = no limit)
 */

/**
 * @typedef {Object} UtilityPartner
 * @property {string} code - Unique partner identifier (e.g., "bc-hydro")
 * @property {string} handle - Partner handle/slug from metaobject
 * @property {string} name - Display name (e.g., "BC Hydro")
 * @property {string} region - Province/state code
 * @property {string} [logoUrl] - Partner logo URL
 * @property {string} [logoSvg] - Partner logo SVG markup
 * @property {string} [logoImg] - Partner image URL (from logo_img field)
 * @property {string} [popupCopy] - Popup copy text (from utility_popup_copy field)
 * @property {string} [widgetDescription] - Short card description (from widget_description field)
 * @property {boolean} [active] - Whether the partner is active
 * @property {number} [rebateAmount] - Rebate value in dollars
 * @property {string} [rebateUrl] - Link to partner rebate application
 * @property {CountryData} [country] - Country data with code/iso
 * @property {ProvinceData} [province] - Province/state data with code/iso
 * @property {Object} [programs] - Rebate and demand response programs
 * @property {Array<{handle: string, type: string, name: string, mysaOfferMode?: string, hasDemandResponse?: boolean, primaryIncentive?: PrimaryIncentive, eligibleProducts?: Array<{productHandle: string, price?: number}>, demandResponseEligibleProducts?: Array<{productHandle: string, price?: number}>, widgetDescription?: string, popupCopy?: string, page_url?: string}>} [programsList] - All active program families
 * @property {string} [mysaOfferMode] - Selected program offer mode (wizard card)
 * @property {PrimaryIncentive} [primaryIncentive] - Winning rebate line for the selected program
 * @property {string} [programType] - Selected program kind (`rebate` or `demand-response`)
 * @property {Object} [customContent] - Additional partner-specific data
 * @property {Record<string, number>} [discountRules] - Product handle to total discount amount (in cents)
 * @property {Record<string, DiscountProgram>} [discountPrograms] - Product handle to program breakdown
 * @property {Record<string, { discountRules: Record<string, number>, discountPrograms: Record<string, DiscountProgram>, rebateQuantityLimit: number }>} [programDiscountRules] - Per utility_program family discounts
 * @property {number} [rebateQuantityLimit] - Maximum quantity eligible for rebate (0 = no limit)
 * @property {number} [mysaDiscountQuantityLimit] - Maximum quantity eligible for Mysa discount (0 = no limit)
 * @property {string} [programHandle] - Selected utility_program family handle (wizard UI)
 * @property {string} [providerName] - Utility display name when card title is a program name
 */

/**
 * @typedef {Object} PrimaryIncentive
 * @property {string} handle - utility_rebate_incentive metaobject handle
 * @property {string} [rebateType] - Line rebate_type (instant_mysa_checkout, etc.)
 * @property {number} [amount] - Raw amount value
 * @property {string} [amountDisplay] - Formatted display amount (e.g. "$75")
 * @property {string} [marketplaceUrl] - Marketplace URL when applicable
 * @property {string} [displayName] - Line display name
 * @property {Array<{productHandle: string, price?: number}>} [eligibleProducts] - Eligible product handles
 */

/**
 * @typedef {Object} EnrichedMatch
 * @property {string} utilityHandle - Original handle from API
 * @property {string} [matchType] - How the match was determined (postal, province, etc.)
 * @property {any} [programs] - Available programs
 * @property {string} code - Provider code
 * @property {string} name - Provider display name
 * @property {string} [logoSvg] - SVG logo markup
 * @property {string} [logoImg] - Image logo URL
 * @property {string} [popupCopy] - Popup copy text
 * @property {string} [widgetDescription] - Short card description (from widget_description field)
 * @property {string} [handle] - Provider handle
 */

/**
 * @typedef {Object} UtilitySelection
 * @property {string} partnerCode - Selected provider metaobject handle (canonical tag id)
 * @property {string} partnerName - Partner display name
 * @property {string} [partnerHandle] - Same as partnerCode; utility_provider metaobject handle
 * @property {string} [programHandle] - Selected utility_program family handle
 * @property {string} [offerMode] - Selected program mysa_offer_mode
 * @property {string} postalCode - User's postal code
 * @property {string} region - Province/state
 * @property {string} selectedAt - ISO timestamp
 * @property {number} [rebateAmount] - Rebate amount if available
 */

// Re-export the pure helpers/constants (defined in `utility-shared.js`, imported
// at the top of this file) so existing `@theme/utility-partner-manager` imports
// keep resolving them.
export {
  UTILITY_BUNDLE_TEMPLATE,
  UTILITY_OFFER_MODE,
  canonicalUtilityHandle,
  isOntarioHrsHandle,
  isHiloHandle,
  appliesOffersCheckoutFloors,
  isUtilityBundleTemplate,
  programCollectionPath,
};

/**
 * Utility Partner Manager
 * Handles partner selection, localStorage persistence, and dynamic content
 */
export class UtilityPartnerManager {
  /** @type {UtilitySelection | null} Current selection */
  #currentSelection = null;

  /** @type {boolean} Whether selection has been loaded */
  #initialized = false;

  /** @type {Promise<void> | null} Guards against concurrent initialization */
  #initPromise = null;

  /** @type {Array<UtilityPartner>} Available utility providers from metaobjects */
  #providers = [];

  /**
   * Utility incentive overrides from variant metafields
   * Keyed by utility_handle (e.g., "bc-hydro", "hydro-quebec")
   * @type {Map<string, UtilityIncentiveOverride>}
   */
  #utilityIncentiveOverrides = new Map();

  /**
   * Product handle for the current page's utility incentive overrides
   * @type {string | null}
   */
  #utilityIncentivesProductHandle = null;

  /**
   * Product price from `data-discount-config` (cents) for the current page
   * product — used when eligible-product payloads omit price.
   * @type {number}
   */
  #discountConfigProductPriceCents = 0;

  /**
   * Product handle from `data-discount-config` for the current page.
   * @type {string | null}
   */
  #discountConfigProductHandle = null;

  /**
   * Normalize a product handle by removing the -legacy suffix
   * This ensures consistent lookups regardless of whether the caller
   * passes 'mysa-baseboard' or 'mysa-baseboard-legacy'
   * @param {string} handle - Product handle to normalize
   * @returns {string} Normalized handle without -legacy suffix
   */
  #normalizeHandle(handle) {
    if (!handle) return "";
    return handle.replace(/-legacy$/, "");
  }

  /**
   * Resolve a storefront price in dollars for price-floor math. Prefers the
   * price inlined with eligible-product payloads; falls back to the current
   * page's `data-discount-config` when handles match.
   * @param {string} handle
   * @param {{ price?: number | string }} [product]
   * @returns {number}
   */
  #resolveEligibleProductPriceDollars(handle, product) {
    const fromProduct = parseFloat(String(product?.price ?? ""));
    if (Number.isFinite(fromProduct) && fromProduct > 0) return fromProduct;

    const normalized = this.#normalizeHandle(handle);
    if (
      this.#discountConfigProductHandle &&
      this.#normalizeHandle(this.#discountConfigProductHandle) === normalized &&
      this.#discountConfigProductPriceCents > 0
    ) {
      return this.#discountConfigProductPriceCents / 100;
    }

    return 0;
  }

  /**
   * Initialize the utility partner manager
   */
  async initialize() {
    // Don't run in Shopify theme editor
    if (window.Shopify?.designMode) {
      log("Utility Partner Manager disabled in theme editor");
      return;
    }

    if (this.#initialized) return;

    // Deduplicate concurrent callers — return the in-flight promise
    // so only one initialization runs at a time.
    if (this.#initPromise) return this.#initPromise;

    this.#initPromise = this.#doInitialize();
    return this.#initPromise;
  }

  async #doInitialize() {
    try {
      // Load utility provider data from metaobjects
      this.#loadProviderData();

      // Load existing selection from localStorage
      this.#loadFromStorage();

      // Check for URL parameters to auto-select providers
      await this.#checkUrlParameters();

      // Restore path: prefer cart program fields over catalog defaults when
      // localStorage only has a partner (common after cookie migration), then
      // fill any remaining gaps from the catalog, then align cart to selection.
      if (this.#currentSelection) {
        await this.#adoptMissingProgramFieldsFromCart();
        this.#hydrateSelectionProgramFields();

        const didSync = await this.#syncCartAttributesFromSelection();
        if (didSync) {
          await this.#waitForCartPropagation();
        }

        document.dispatchEvent(
          new UtilityPartnerLoadedEvent(this.#currentSelection),
        );
      }

      this.#initialized = true;
    } catch (error) {
      console.error("Failed to initialize Utility Partner Manager:", error);
      reportError(error, { tags: { op: "init" } });
    } finally {
      this.#initPromise = null;
    }
  }

  /**
   * Load utility provider data from JSON script tag
   */
  #loadProviderData() {
    const script = document.querySelector(
      'script[data-utility-providers][type="application/json"]',
    );

    if (!script || !script.textContent) {
      log(
        "Utility provider data not found. Make sure utility-provider-data.liquid is rendered.",
      );
      return;
    }

    try {
      const data = JSON.parse(script.textContent);
      this.#providers = data.providers || [];

      // Load utility incentive overrides from discount config (if available)
      this.#loadUtilityIncentiveOverrides();

      // Process eligible products for each provider
      for (const provider of this.#providers) {
        this.#processProviderPrograms(provider);
      }

      log(
        `Loaded ${this.#providers.length} utility providers from metaobjects`,
      );

      // for (const provider of this.#providers) {
      //   console.log(provider);
      // }
    } catch (error) {
      console.error("Failed to parse utility provider data:", error);
      reportError(error, { tags: { op: "parse-providers" } });
    }
  }

  /**
   * Load utility incentive overrides from DiscountConfig (variant metafield data)
   * Structure: [{utility_handle, instant_rebate: {...}, demand_response: {...}, mysa_discount: {...}}]
   */
  #loadUtilityIncentiveOverrides() {
    const configScript = document.querySelector(
      'script[type="application/json"][data-discount-config]',
    );
    if (!configScript?.textContent) {
      return;
    }

    let discountConfig;
    try {
      discountConfig = JSON.parse(configScript.textContent);
    } catch {
      return;
    }

    try {
      if (discountConfig.productHandle && discountConfig.productPrice > 0) {
        this.#discountConfigProductHandle = String(
          discountConfig.productHandle,
        );
        this.#discountConfigProductPriceCents = Number(
          discountConfig.productPrice,
        );
      }

      const utilityIncentives = discountConfig.utilityIncentives;

      if (!utilityIncentives?.hasData || !utilityIncentives.data) {
        return;
      }

      // Store the product handle for validation when applying overrides
      this.#utilityIncentivesProductHandle = utilityIncentives.productHandle;

      // Parse the data (may be stringified JSON)
      let incentivesData = utilityIncentives.data;
      if (typeof incentivesData === "string") {
        incentivesData = JSON.parse(incentivesData);
      }

      if (!Array.isArray(incentivesData)) {
        return;
      }

      // Build map of utility_handle -> incentive config
      // Supports both old format (instant_rebate, mysa_discount) and new format (rebate, mysa)
      for (const incentive of incentivesData) {
        // Handle both utility_partner_handle and utility_handle
        const utilityHandle =
          incentive.utility_partner_handle || incentive.utility_handle;
        if (!utilityHandle) continue;

        // Map new metafield format to internal structure
        // New format: rebate, dr, mysa
        // Old format: instant_rebate, demand_response, mysa_discount
        const rebateData = incentive.rebate || incentive.instant_rebate;
        const mysaData = incentive.mysa || incentive.mysa_discount;
        const drData = incentive.dr || incentive.demand_response;

        const incentiveConfig = {
          // Store rebate config with stacking flags
          instant_rebate: rebateData
            ? {
                enabled: true,
                rebate_amount:
                  rebateData.amount || rebateData.rebate_amount || 0,
                price_floor: rebateData.price_floor || null,
                rebate_override: rebateData.rebate_override || null,
                max_quantity: rebateData.max_quantity || 0,
                include_byob: rebateData.include_byob !== false, // Default to true if not specified
                // general_discount_id: null means don't stack general discounts
                include_general: rebateData.general_discount_id != null,
              }
            : undefined,
          demand_response: drData
            ? {
                enabled: true,
                discount_amount: drData.discount_amount || drData.amount || 0,
              }
            : undefined,
          mysa_discount: mysaData
            ? {
                enabled: true,
                discount_override:
                  mysaData.amount || mysaData.discount_override || 0,
                quantity_limit: mysaData.quantity || 0,
              }
            : undefined,
        };

        this.#utilityIncentiveOverrides.set(
          canonicalUtilityHandle(utilityHandle),
          incentiveConfig,
        );

        log(`Loaded utility incentive override for ${utilityHandle}:`, {
          rebate: rebateData,
          mysa: mysaData,
          include_byob: rebateData?.include_byob,
        });
      }

      if (this.#utilityIncentiveOverrides.size > 0) {
        log(
          `Loaded ${this.#utilityIncentiveOverrides.size} utility incentive override(s) for ${this.#utilityIncentivesProductHandle}`,
        );
      }
    } catch (error) {
      console.error("Failed to parse utility incentive overrides:", error);
      reportError(error, { tags: { op: "parse-incentive-overrides" } });
    }
  }

  /**
   * Get utility incentive override for a provider code and product handle
   * @param {string} providerCode - Provider code/handle (e.g., "bc-hydro", "hydro-quebec")
   * @param {string} productHandle - Product handle to check
   * @returns {UtilityIncentiveOverride | null}
   */
  #getUtilityIncentiveOverride(providerCode, productHandle) {
    // Offers JSON is page-scoped (from data-discount-config on the PDP).
    // Without it, JS cannot resolve amounts — non-PDP relies on Liquid SSR.
    if (!this.#utilityIncentivesProductHandle) return null;

    const normalizedHandle = this.#normalizeHandle(productHandle);
    const normalizedMetafieldHandle = this.#normalizeHandle(
      this.#utilityIncentivesProductHandle,
    );

    if (normalizedHandle !== normalizedMetafieldHandle) return null;

    // v2: provider.code and provider.handle both mirror the metaobject handle
    let override = this.#utilityIncentiveOverrides.get(
      canonicalUtilityHandle(providerCode),
    );

    if (!override) {
      const provider = this.getProviderByCode(providerCode);
      if (provider?.handle && provider.handle !== providerCode) {
        override = this.#utilityIncentiveOverrides.get(provider.handle);
      }
      if (!override && provider?.code && provider.code !== providerCode) {
        override = this.#utilityIncentiveOverrides.get(provider.code);
      }
    }

    return override || null;
  }

  /**
   * Whether instant-checkout offers pricing is configured for this provider/product.
   * True when page-scoped `$app:offers` enables an instant rebate, or when
   * discountRules were already built for the product from that data.
   * @param {string} providerCode
   * @param {string} productHandle
   * @returns {boolean}
   */
  #hasConfiguredUtilityIncentive(providerCode, productHandle) {
    const override = this.#getUtilityIncentiveOverride(
      providerCode,
      productHandle,
    );
    if (override?.instant_rebate?.enabled) return true;

    // Trust rules already built for this product (PDP offers path).
    const provider = this.getProviderByCode(providerCode);
    if (!provider) return false;

    const { discountRules } = this.#getActiveDiscountContext(provider);
    const normalizedHandle = this.#normalizeHandle(productHandle);
    return Boolean(discountRules?.[normalizedHandle]);
  }

  /**
   * @typedef {Object} NestedRebate
   * @property {string} program_handle
   * @property {string} mysaOfferMode
   * @property {Array<any>} eligible_products - Legacy field, always empty in v2
   * @property {Array<{productHandle: string, price?: number}>} eligible_products_v2
   * @property {string} page_url
   */

  /**
   * @typedef {Object} NestedDemandResponse
   * @property {string} handle
   * @property {string} program_name
   * @property {Array<any>} eligibleProducts - Legacy; merged in #processProviderPrograms
   * @property {Array<{productHandle: string, price?: number}>} eligibleProductsV2
   * @property {string} page_url
   */

  /**
   * @typedef {Object} NestedPrograms
   * @property {NestedRebate} [rebate]
   * @property {NestedDemandResponse} [demandResponse]
   */

  /**
   * Rebuild the nested `programs` view from `programsList`.
   *
   * v2 stopped serializing `programs` in `utility-provider-data.liquid` because
   * it duplicated the first rebate/DR family already present in `programsList`.
   * This reconstructs the same object client-side so no consumer has to change.
   *
   * IMPORTANT: `NestedPrograms` is the canonical shape for this view. The legacy
   * (pre-v2) fallback branch of `utility-provider-data.liquid` emits the SAME
   * shape inline for old providers, so both emitters must stay in sync with the
   * typedef above. The mixed snake_case/camelCase is a frozen legacy wire shape
   * (do not migrate piecemeal).
   *
   * Field parity with the former blob:
   * - rebate: first family with an offer mode + primary incentive; its
   *   `primaryIncentive.eligibleProducts` becomes `eligible_products_v2`.
   * - demandResponse: first family carrying demand-response incentives;
   *   `demandResponseEligibleProducts` is the first DR line only (not the
   *   family union, which also includes rebate products).
   *
   * @param {UtilityPartner} provider - Provider object (with `programsList`)
   * @returns {NestedPrograms}
   */
  #buildNestedProgramsFromList(provider) {
    /** @type {NestedPrograms} */
    const programs = {};

    const rebateFamily = provider.programsList.find(
      (family) =>
        family.type === "rebate" &&
        family.mysaOfferMode &&
        family.primaryIncentive,
    );
    if (rebateFamily) {
      const primary = rebateFamily.primaryIncentive;
      programs.rebate = {
        program_handle: rebateFamily.handle,
        mysaOfferMode: rebateFamily.mysaOfferMode || "",
        eligible_products: [],
        eligible_products_v2: primary?.eligibleProducts || [],
        page_url:
          rebateFamily.page_url || programCollectionPath(rebateFamily.handle),
      };
    }

    // A family typed 'rebate' can also carry DR incentives; match the DR type OR
    // the serialized `hasDemandResponse` flag so a combined family still rebuilds
    // `demandResponse` — parity with the pre-v2 blob's independent rebate/DR scan.
    const drFamily = provider.programsList.find(
      (family) => family.type === "demand-response" || family.hasDemandResponse,
    );
    if (drFamily) {
      programs.demandResponse = {
        handle: drFamily.handle,
        program_name: drFamily.name,
        eligibleProducts: [],
        eligibleProductsV2: drFamily.demandResponseEligibleProducts || [],
        page_url: drFamily.page_url || programCollectionPath(drFamily.handle),
      };
    }

    return programs;
  }

  /**
   * Process provider programs and normalize product handles
   * @param {UtilityPartner} provider - Provider object
   */
  #processProviderPrograms(provider) {
    // Nested `programs` is a derived view of the first rebate/DR family and is
    // no longer serialized in the v2 blob (removes a ~16 KB/page duplicate of
    // `programsList`). Rebuild it here from `programsList` so every downstream
    // consumer (provider-wide discount rules, learn-more URLs, offer-mode
    // fallback, wizard fallback card) sees the same shape as before. Legacy
    // providers ship `programs` inline and have no `programsList`.
    if (Array.isArray(provider.programsList) && provider.programsList.length) {
      provider.programs = this.#buildNestedProgramsFromList(provider);
    }

    // Process demand response program eligible products
    const demandResponse = /** @type {any} */ (provider.programs)
      ?.demandResponse;

    if (demandResponse) {
      // Merge v2 product-reference entries into the legacy eligibleProducts array
      // so downstream logic only iterates one list. Dedup by productHandle in case
      // the same product is configured in both fields during the transition.
      demandResponse.eligibleProducts = this.#mergeEligibleProducts(
        demandResponse.eligibleProducts,
        demandResponse.eligibleProductsV2,
      );

      // Normalize product handles by removing '-legacy' suffix
      for (const product of demandResponse.eligibleProducts) {
        if (product.productHandle) {
          product.productHandle = product.productHandle.replace(/-legacy$/, "");
        }
      }
    }

    // Process rebate program eligible products and calculate rebate amounts
    const rebate = /** @type {any} */ (provider.programs)?.rebate;

    if (rebate) {
      // Merge v2 product-reference entries into the legacy eligible_products array.
      rebate.eligible_products = this.#mergeEligibleProducts(
        rebate.eligible_products,
        rebate.eligible_products_v2,
      );
    }

    if (rebate?.eligible_products?.length) {
      const products = rebate.eligible_products;

      // Normalize product handles by removing '-legacy' suffix
      for (const product of products) {
        if (product.productHandle) {
          product.productHandle = product.productHandle.replace(/-legacy$/, "");
        }
      }

      // Theme discount pricing uses `$app:offers` utility-incentives only — not
      // metaobject amounts. Same contract as `appliesOffersCheckoutFloors()`.
      if (appliesOffersCheckoutFloors(provider.code, rebate.mysaOfferMode)) {
        this.#buildRebateDiscountRules(provider, rebate);
      }
    }

    this.#buildProgramFamilyDiscountRules(provider);
  }

  /**
   * Merge legacy JSON eligible-products entries with new product-reference v2
   * entries. The v2 list emits minimal `{ productHandle }` objects from
   * `utility-provider-data.liquid`; legacy entries may carry extra fields and
   * are preserved verbatim. Entries are deduped by `productHandle` so a
   * product configured in both fields during the transition appears once.
   *
   * @param {Array<any> | undefined} legacy - Legacy eligible products array
   * @param {Array<any> | undefined} v2 - V2 product-reference array
   * @returns {Array<any>} Merged array (empty if both inputs are empty)
   */
  #mergeEligibleProducts(legacy, v2) {
    const legacyList = Array.isArray(legacy) ? legacy : [];
    const v2List = Array.isArray(v2) ? v2 : [];
    if (!v2List.length) return legacyList;

    const seen = new Set(
      legacyList.map((p) => p?.productHandle).filter(Boolean),
    );
    const additions = v2List.filter(
      (p) => p?.productHandle && !seen.has(p.productHandle),
    );
    return additions.length ? [...legacyList, ...additions] : legacyList;
  }

  /**
   * Build discount rules from `$app:offers` for eligible products on a rebate
   * program. Amounts come from offers incentives; the metaobject rebate only
   * supplies eligible product handles and quantity limits.
   * @param {UtilityPartner} provider - Provider object
   * @param {any} rebate - Rebate program data
   */
  #buildRebateDiscountRules(provider, rebate) {
    // Store quantity limit for this provider's rebate program
    // Parse as integer to handle string values from API
    const quantityLimit = rebate.quantity_limit
      ? parseInt(rebate.quantity_limit, 10)
      : 0;
    provider.rebateQuantityLimit = isNaN(quantityLimit) ? 0 : quantityLimit;

    // Debug logging (only in development)
    if (provider.rebateQuantityLimit > 0) {
      log(
        `UtilityPartnerManager: Set rebateQuantityLimit for ${provider.code}: ${provider.rebateQuantityLimit}`,
      );
    }

    // Note: Instant rebate dollars, price floors, Mysa discounts, and stacking
    // flags come from `$app:offers` utility-incentives (same source as Liquid
    // SSR). Metaobject amounts are not used for displayed theme pricing.
    // Ontario HRS (enbridge) is a hardcoded Liquid outlier — skip it above.

    // Initialize discount structures
    provider.discountRules = provider.discountRules || {};
    provider.discountPrograms = provider.discountPrograms || {};

    if (!Array.isArray(rebate.eligible_products)) return;

    // Process each eligible product — only when page-scoped `$app:offers`
    // (data-discount-config) configures an instant rebate for that product.
    // Collection/homepage/cart pages have no offers JSON in JS; Liquid SSR
    // prices those surfaces and section morph refreshes after utility changes.
    for (const product of rebate.eligible_products) {
      const handle = product.productHandle;
      if (!handle) continue;

      const override = this.#getUtilityIncentiveOverride(provider.code, handle);
      if (!override?.instant_rebate?.enabled) continue;

      // ============================================
      // METAFIELD OVERRIDES ($app:offers)
      // ============================================
      let priceFloorCents = null;
      let mysaDiscountCents = 0;
      let rebateMultiplier = 1;

      const instantRebate = override.instant_rebate;
      const offersAmountDollars = Number(instantRebate.rebate_amount);
      let productRebateInCents =
        Number.isFinite(offersAmountDollars) && offersAmountDollars > 0
          ? Math.round(offersAmountDollars * 100)
          : 0;

      if (instantRebate.price_floor != null) {
        priceFloorCents = Math.round(instantRebate.price_floor * 100);
        log(
          `Using metafield price_floor override for ${provider.code}/${handle}: $${instantRebate.price_floor}`,
        );
      }

      if (
        override.mysa_discount?.enabled &&
        override.mysa_discount.discount_override != null
      ) {
        mysaDiscountCents = Math.round(
          override.mysa_discount.discount_override * 100,
        );
        log(
          `Using metafield mysa_discount override for ${provider.code}/${handle}: $${override.mysa_discount.discount_override}`,
        );
      }

      if (instantRebate.rebate_override != null) {
        const rebateOverride = instantRebate.rebate_override;
        if (rebateOverride > 10) {
          productRebateInCents = Math.round(rebateOverride * 100);
          log(
            `Using metafield rebate_override amount for ${provider.code}/${handle}: $${rebateOverride}`,
          );
        } else if (rebateOverride > 0) {
          rebateMultiplier = rebateOverride;
          productRebateInCents = Math.round(
            productRebateInCents * rebateMultiplier,
          );
          log(
            `Using metafield rebate_override multiplier for ${provider.code}/${handle}: ${rebateMultiplier}x`,
          );
        }
      } else if (productRebateInCents > 0) {
        log(
          `Using $app:offers rebate_amount for ${provider.code}/${handle}: $${offersAmountDollars}`,
        );
      }

      let includeBYOB = true;
      let includeGeneral = true;

      if (instantRebate.include_byob === false) {
        includeBYOB = false;
        log(
          `BYOB excluded for ${provider.code}/${handle} (include_byob: false)`,
        );
      }

      if (instantRebate.include_general === false) {
        includeGeneral = false;
        log(
          `General discount excluded for ${provider.code}/${handle} (general_discount_id: null)`,
        );
      }

      const maxQuantity = instantRebate.max_quantity || 0;

      // Initialize program breakdown for this product
      provider.discountPrograms[handle] = {
        rebate: productRebateInCents,
        mysaDiscount: mysaDiscountCents,
        total: productRebateInCents + mysaDiscountCents,
        providerName: provider.name,
        priceFloor: priceFloorCents, // null if no floor, cents if floor exists
        includeBYOB: includeBYOB, // Whether BYOB discount can stack with this utility
        includeGeneral: includeGeneral, // Whether general discount can stack with this utility
        maxQuantity: maxQuantity, // Max quantity for rebate (0 = no limit)
      };

      // Start with rebate + mysa discount
      let totalDiscount = productRebateInCents + mysaDiscountCents;

      // Check if there's a price floor for this provider/product and apply it
      if (priceFloorCents !== null) {
        const productPrice = this.#resolveEligibleProductPriceDollars(
          handle,
          product,
        );
        const productPriceCents = Math.round(productPrice * 100);

        // Without a known storefront price, productPriceCents is 0 so
        // maxDiscount goes negative and Math.max(0, …) collapses the discount
        // (fail closed — same as main). Do not skip the floor.
        const maxDiscountCents = productPriceCents - priceFloorCents;

        // If total discount exceeds max, we need to reduce it
        if (totalDiscount > maxDiscountCents) {
          totalDiscount = Math.max(0, maxDiscountCents);

          // Proportionally reduce rebate and mysa discount to fit within max
          const originalTotal =
            productRebateInCents +
            provider.discountPrograms[handle].mysaDiscount;

          if (originalTotal > 0) {
            const ratio = maxDiscountCents / originalTotal;
            provider.discountPrograms[handle].rebate = Math.round(
              productRebateInCents * ratio,
            );
            provider.discountPrograms[handle].mysaDiscount = Math.round(
              provider.discountPrograms[handle].mysaDiscount * ratio,
            );
          }
        }
      }

      // Update total in program breakdown
      provider.discountPrograms[handle].total = totalDiscount;

      // Only add positive discounts to discountRules (for backwards compatibility)
      if (totalDiscount > 0) {
        provider.discountRules[handle] = totalDiscount;
      }
    }
  }

  /**
   * Per-program-family discount rules for multi-program providers (e.g. PSE).
   * Uses each family's primary instant-checkout incentive and the same
   * `$app:offers` gating as `#buildRebateDiscountRules`.
   * @param {UtilityPartner} provider
   */
  #buildProgramFamilyDiscountRules(provider) {
    if (
      isOntarioHrsHandle(provider.code) ||
      !Array.isArray(provider.programsList) ||
      !provider.programsList.length
    ) {
      return;
    }

    /** @type {Record<string, { discountRules: Record<string, number>, discountPrograms: Record<string, DiscountProgram>, rebateQuantityLimit: number }>} */
    const programDiscountRules = {};

    for (const family of provider.programsList) {
      if (!appliesOffersCheckoutFloors(provider.code, family.mysaOfferMode)) {
        continue;
      }

      const primary = family.primaryIncentive;
      // Prefer family-wide union (all rebate lines). Primary alone misses
      // Fortis Instant's second line (baseboard / AC / in-floor).
      const eligibleProducts =
        Array.isArray(family.eligibleProducts) && family.eligibleProducts.length
          ? family.eligibleProducts
          : primary?.eligibleProducts;
      if (!Array.isArray(eligibleProducts) || !eligibleProducts.length) {
        continue;
      }

      // Amounts come from `$app:offers` inside `#buildRebateDiscountRules`;
      // do not gate on metaobject `primary.amount` (display-only).
      /** @type {UtilityPartner} */
      const scratch = {
        code: provider.code,
        handle: provider.handle,
        name: provider.name,
        discountRules: {},
        discountPrograms: {},
      };

      this.#buildRebateDiscountRules(scratch, {
        quantity_limit: 0,
        eligible_products: eligibleProducts.map((product) => ({
          productHandle: String(product.productHandle || "").replace(
            /-legacy$/,
            "",
          ),
          price: Number(product.price || 0),
        })),
        amount_pre_tax: Number(primary?.amount || 0),
        amount_post_tax: Number(primary?.amount || 0),
      });

      if (
        !scratch.discountRules ||
        !Object.keys(scratch.discountRules).length
      ) {
        continue;
      }

      programDiscountRules[family.handle] = {
        discountRules: scratch.discountRules || {},
        discountPrograms: scratch.discountPrograms || {},
        rebateQuantityLimit: scratch.rebateQuantityLimit || 0,
      };
    }

    if (Object.keys(programDiscountRules).length > 0) {
      provider.programDiscountRules = programDiscountRules;
    }
  }

  /**
   * Discount rules for the current selection — program-scoped when a
   * utility_program handle is persisted, otherwise provider-wide.
   * When a program is selected but has no entry in programDiscountRules
   * (stale handle, post-purchase/marketplace family, etc.), fail closed
   * instead of falling back to provider-wide rules from another family.
   * @param {UtilityPartner} provider
   * @returns {{ discountRules?: Record<string, number>, discountPrograms?: Record<string, DiscountProgram>, rebateQuantityLimit?: number }}
   */
  #getActiveDiscountContext(provider) {
    const programHandle = this.#currentSelection?.programHandle;

    if (programHandle && provider.programDiscountRules) {
      const direct = provider.programDiscountRules[programHandle];
      if (direct) return direct;

      const family = this.resolveProgramFamily(provider, programHandle);
      if (family?.handle) {
        const resolved = provider.programDiscountRules[family.handle];
        if (resolved) return resolved;
      }

      log(`No program discount context for ${programHandle}; failing closed`);
      return {
        discountRules: {},
        discountPrograms: {},
        rebateQuantityLimit: 0,
      };
    }

    return {
      discountRules: provider.discountRules,
      discountPrograms: provider.discountPrograms,
      rebateQuantityLimit: provider.rebateQuantityLimit,
    };
  }

  /**
   * Short code mapping for URL-based utility selection
   * Usage: ?utility=bch → Auto-selects BC Hydro
   */
  static UTILITY_SHORT_CODES = {
    bch: "bc-hydro",
    nbp: "new-brunswick-power",
    ens: "efficiency-nova-scotia",
    sje: "saint-john-energy",
    pei: "efficiency-pei",
    mnp: "minnesota-power",
    scl: "seattle-city-light",
    qch: "hydro-quebec",
    bnw: "new-westminster",
  };

  /**
   * Default postal codes by provider for auto-selection
   */
  static DEFAULT_POSTAL_CODES = {
    "bc-hydro": "V6B2W2", // Vancouver, BC
    "new-brunswick-power": "E3B1A1", // Fredericton, NB
    "efficiency-nova-scotia": "B3H4R2", // Halifax, NS
    "saint-john-energy": "E2L4S6", // Saint John, NB
    "efficiency-pei": "C1A4P3", // Charlottetown, PEI
    "minnesota-power": "55802", // Duluth, MN
    "seattle-city-light": "98101", // Seattle, WA
    "hydro-quebec": "H2Y1C6", // Montreal, QC
    "new-westminster": "V3L3C2", // New Westminster, BC
  };

  /**
   * Check URL parameters for auto-selection of utility providers
   * Supports both legacy ?sje=1 format and new ?utility=XXX short codes
   */
  async #checkUrlParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const programHandle = urlParams.get("program") || "";

    // Priority 1: ?utility= short code or provider handle (rebate list Buy now)
    const utilityCode = urlParams.get("utility");
    if (utilityCode) {
      const handled = await this.#handleUtilityShortCode(
        utilityCode,
        programHandle,
      );
      if (handled) {
        this.#clearQueryParam("utility");
        this.#clearQueryParam("program");
        return;
      }
    }

    // Priority 2: Legacy ?sje=1 parameter (backwards compatibility)
    if (urlParams.get("sje") === "1") {
      const handled = await this.#handleUtilityShortCode("sje", programHandle);
      if (handled) {
        this.#clearQueryParam("sje");
        this.#clearQueryParam("program");
      }
    }
  }

  /**
   * Handle utility selection from a short code or provider handle.
   * When `programHandle` is present (rebate-list Buy now), stamp that family's
   * `mysaOfferMode` so multi-program utilities do not keep a sibling mode.
   * @param {string} shortCode - Short code (e.g. 'bch') or metaobject handle
   * @param {string} [programHandle] - Optional utility_program family handle
   * @returns {Promise<boolean>} Whether selection was successful
   */
  async #handleUtilityShortCode(shortCode, programHandle = "") {
    const normalized = shortCode.toLowerCase().trim();
    const handle =
      UtilityPartnerManager.UTILITY_SHORT_CODES[normalized] || normalized;

    const provider = this.getProviderByCode(handle);

    if (!provider) {
      log(`Provider not found for handle: ${handle}`);
      return false;
    }

    const existingData = UtilityStorage.get();
    const defaultPostal =
      UtilityPartnerManager.DEFAULT_POSTAL_CODES[handle] ||
      UtilityPartnerManager.DEFAULT_POSTAL_CODES[provider.handle] ||
      "";
    const postalCode = existingData?.postalCode?.value || defaultPostal;

    log(`Auto-selecting ${provider.name} from URL parameter`);

    const familyHandle = programHandle.trim();
    let partner = provider;
    if (familyHandle) {
      const family = this.resolveProgramFamily(provider, familyHandle);
      partner = {
        ...provider,
        programHandle: familyHandle,
        // Family mode is the source of truth. Spreading the catalog provider
        // can copy a stale wizard-mutated mysaOfferMode from a sibling family.
        mysaOfferMode: family?.mysaOfferMode || "",
      };
    }

    await this.setPartner(partner, postalCode);

    return true;
  }

  /**
   * Remove a query parameter from URL without page reload
   * @param {string} param - Parameter name to remove
   */
  #clearQueryParam(param) {
    const url = new URL(window.location.href);
    url.searchParams.delete(param);
    window.history.replaceState({}, "", url.toString());
    log(`Cleared ${param} query parameter`);
  }

  /**
   * Load selection from localStorage
   */
  #loadFromStorage() {
    const data = UtilityStorage.get();

    if (!data?.utility) {
      log("No utility selection found");
      return;
    }

    const partnerCode = canonicalUtilityHandle(data.utility.code);
    this.#currentSelection = {
      partnerCode,
      partnerHandle: partnerCode,
      partnerName: data.utility.name,
      programHandle: data.utility.programHandle
        ? canonicalUtilityHandle(data.utility.programHandle)
        : data.utility.programHandle,
      offerMode: data.utility.offerMode || "",
      postalCode: data.postalCode?.value || "",
      region: data.utility.region,
      selectedAt: data.utility.selectedAt,
      rebateAmount: data.utility.rebateAmount || 0,
    };

    log("Loaded utility selection from storage:", this.#currentSelection);
  }

  /**
   * Persist programHandle / offerMode onto the in-memory selection and storage
   * when either field changes.
   * @param {string} [programHandle]
   * @param {string} [offerMode]
   */
  #persistSelectionProgramFields(programHandle, offerMode) {
    const selection = this.#currentSelection;
    if (!selection) return;

    const nextProgram = programHandle || "";
    const nextMode = offerMode || "";
    const programChanged =
      Boolean(nextProgram) && nextProgram !== (selection.programHandle || "");
    const modeChanged =
      Boolean(nextMode) && nextMode !== (selection.offerMode || "");
    if (!programChanged && !modeChanged) return;

    if (programChanged) selection.programHandle = nextProgram;
    if (modeChanged) selection.offerMode = nextMode;

    const data = UtilityStorage.get();
    if (data?.utility) {
      if (programChanged) data.utility.programHandle = nextProgram;
      if (modeChanged) data.utility.offerMode = nextMode;
      UtilityStorage.set(data);
    }
  }

  /**
   * When restored localStorage lacks program fields, adopt them from cart if
   * the cart partner matches. Must run before catalog defaults — otherwise
   * multi-program providers get the first instant family and overwrite the
   * buyer's wizard-selected cart program on every page load.
   * @returns {Promise<void>}
   */
  async #adoptMissingProgramFieldsFromCart() {
    const selection = this.#currentSelection;
    if (!selection?.partnerCode) return;
    if (selection.programHandle && selection.offerMode) return;

    const provider = this.getProviderByCode(selection.partnerCode);
    if (!provider) return;

    const partnerHandle = provider.handle || provider.code;

    /** @type {Record<string, string>} */
    let attrs = {};
    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        credentials: "same-origin",
      });
      if (!cartResponse.ok) return;
      const currentCart = await cartResponse.json();
      attrs = currentCart.attributes || {};
    } catch (error) {
      log("Cart adopt pre-check failed:", error);
      return;
    }

    const cartPartner = attrs._utility_partner_code || "";
    if (cartPartner && cartPartner !== partnerHandle) return;

    const cartProgram = attrs._utility_program_code || "";
    const cartMode = attrs._utility_offer_mode || "";
    if (!cartProgram && !cartMode) return;

    this.#persistSelectionProgramFields(
      selection.programHandle || cartProgram,
      selection.offerMode || cartMode,
    );

    log(
      `Adopted program fields from cart onto restored selection: programHandle=${
        selection.programHandle || ""
      } offerMode=${selection.offerMode || ""}`,
    );
  }

  /**
   * Fill still-missing programHandle / offerMode from catalog defaults (same
   * path as `setPartner`). Call only after cart adopt so wizard-selected cart
   * programs win over `#defaultProgramSelection`.
   */
  #hydrateSelectionProgramFields() {
    if (!this.#currentSelection?.partnerCode) return;

    const selection = this.#currentSelection;
    if (selection.programHandle && selection.offerMode) return;

    const provider = this.getProviderByCode(selection.partnerCode);
    if (!provider) return;

    const { programHandle, mysaOfferMode } = this.#resolvePartnerProgramFields(
      {
        programHandle: selection.programHandle,
        mysaOfferMode: selection.offerMode,
      },
      provider,
    );

    if (!programHandle && !mysaOfferMode) return;

    this.#persistSelectionProgramFields(
      selection.programHandle || programHandle,
      selection.offerMode || mysaOfferMode,
    );

    log(
      `Hydrated program fields from catalog onto restored selection: programHandle=${
        selection.programHandle || ""
      } offerMode=${selection.offerMode || ""}`,
    );
  }

  /**
   * Ensure cart `_utility_*` attributes match the in-memory selection.
   * Returns true when a cart write was performed.
   * Selection is authoritative after cart-adopt + catalog hydrate; never write
   * blanks over existing cart program/offer attrs.
   * @returns {Promise<boolean>}
   */
  async #syncCartAttributesFromSelection() {
    const selection = this.#currentSelection;
    if (!selection?.partnerCode) return false;

    const provider = this.getProviderByCode(selection.partnerCode);
    if (!provider) {
      log(
        `Cannot sync cart attributes — provider not found: ${selection.partnerCode}`,
      );
      return false;
    }

    const utilityPartnerHandle = provider.handle || provider.code;

    /** @type {Record<string, string>} */
    let attrs = {};

    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        credentials: "same-origin",
      });

      if (!cartResponse.ok) {
        throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      }

      const currentCart = await cartResponse.json();
      attrs = currentCart.attributes || {};
    } catch (error) {
      log("Cart attribute pre-check failed, continuing with sync:", error);
    }

    // Selection is source of truth after adopt/hydrate. Fall back to cart only
    // when selection still lacks a field (e.g. cart fetch failed during adopt).
    const utilityProgramHandle =
      selection.programHandle || attrs._utility_program_code || "";
    const utilityOfferMode =
      selection.offerMode || attrs._utility_offer_mode || "";

    if (
      !selection.programHandle &&
      utilityProgramHandle &&
      (!attrs._utility_partner_code ||
        attrs._utility_partner_code === utilityPartnerHandle)
    ) {
      this.#persistSelectionProgramFields(
        utilityProgramHandle,
        selection.offerMode || utilityOfferMode,
      );
    } else if (!selection.offerMode && utilityOfferMode) {
      this.#persistSelectionProgramFields(
        selection.programHandle || utilityProgramHandle,
        utilityOfferMode,
      );
    }

    if (
      attrs._utility_partner_code === utilityPartnerHandle &&
      (attrs._utility_program_code || "") === utilityProgramHandle &&
      (attrs._utility_offer_mode || "") === utilityOfferMode &&
      attrs._rebate_prequalify === "1"
    ) {
      log("Cart attributes already match restored selection");
      return false;
    }

    // Refuse to clear a matching cart's program/mode with blanks
    if (
      attrs._utility_partner_code === utilityPartnerHandle &&
      (!utilityProgramHandle || !utilityOfferMode)
    ) {
      log(
        "Skipping cart sync — selection still missing program fields and would clear cart",
      );
      return false;
    }

    await this.#setCartAttributes({
      name: provider.name,
      handle: utilityPartnerHandle,
      programHandle: utilityProgramHandle,
      mysaOfferMode: utilityOfferMode,
    });

    log("Synced cart attributes from restored utility selection");
    return true;
  }

  /**
   * Resolve programHandle + mysaOfferMode for a selection payload.
   * Wizard cards pass both; Buy now passes `?utility=` + `?program=` (family
   * handle only). A raw catalog provider may already carry a stale
   * `mysaOfferMode` from the wizard mutating the in-memory object.
   * @param {UtilityPartner | { programHandle?: string, mysaOfferMode?: string }} partner
   * @param {UtilityPartner | null | undefined} catalogProvider
   * @returns {{ programHandle: string, mysaOfferMode: string }}
   */
  #resolvePartnerProgramFields(partner, catalogProvider) {
    let programHandle = partner.programHandle || "";
    let mysaOfferMode = partner.mysaOfferMode || "";
    const provider = catalogProvider || partner;

    if (programHandle) {
      const family = this.resolveProgramFamily(provider, programHandle);
      if (family) {
        // Chosen family wins over a stale provider-level mode. Blank is
        // intentional (DR / post-purchase) and must not inherit a sibling's
        // instant_checkout mode.
        mysaOfferMode = family.mysaOfferMode || "";
      }
    } else {
      const defaults = this.#defaultProgramSelection(provider);
      programHandle = defaults.programHandle;
      mysaOfferMode = defaults.mysaOfferMode;
    }

    return { programHandle, mysaOfferMode };
  }

  /**
   * Default program + offer mode from the provider catalog when the caller
   * did not pick a wizard card (e.g. marketing ?utility= links).
   * Prefers the first instant_checkout family, then any family with a mode,
   * then nested programs.rebate.
   * @param {UtilityPartner | null | undefined} provider
   * @returns {{ programHandle: string, mysaOfferMode: string }}
   */
  #defaultProgramSelection(provider) {
    if (!provider) return { programHandle: "", mysaOfferMode: "" };

    if (Array.isArray(provider.programsList) && provider.programsList.length) {
      const instant = provider.programsList.find(
        (family) =>
          family.mysaOfferMode === UTILITY_OFFER_MODE.INSTANT_CHECKOUT,
      );
      const withMode = provider.programsList.find(
        (family) => family.mysaOfferMode,
      );
      const family = instant || withMode || provider.programsList[0];

      if (family?.handle) {
        return {
          programHandle: family.handle,
          mysaOfferMode: family.mysaOfferMode || "",
        };
      }
    }

    const rebate = provider.programs?.rebate;
    if (rebate?.program_handle || rebate?.mysaOfferMode) {
      return {
        programHandle: rebate.program_handle || "",
        mysaOfferMode: rebate.mysaOfferMode || "",
      };
    }

    return { programHandle: "", mysaOfferMode: "" };
  }

  /**
   * Set utility partner selection
   * @param {UtilityPartner} partner - Partner data
   * @param {string} postalCode - User's postal code
   * @param {Object} [extraAttributes] - Optional extra cart attributes to set (e.g., { _should_receive_discount: "1" })
   */
  async setPartner(partner, postalCode, extraAttributes = {}) {
    const providerHandle = partner?.handle || partner?.code;
    if (!partner || !providerHandle) {
      console.error("Invalid partner data");
      return false;
    }

    const normalizedPostalCode = postalCode.replace(/\s/g, "").toUpperCase();
    const selectedAt = new Date().toISOString();
    const catalogProvider = this.getProviderByCode(providerHandle);
    const { programHandle, mysaOfferMode } = this.#resolvePartnerProgramFields(
      partner,
      catalogProvider,
    );

    // Create selection object
    this.#currentSelection = {
      partnerCode: providerHandle,
      partnerHandle: providerHandle,
      partnerName: partner.name,
      programHandle,
      offerMode: mysaOfferMode,
      postalCode: normalizedPostalCode,
      region: partner.region,
      selectedAt,
      rebateAmount: partner.rebateAmount,
    };

    const providerCountry =
      partner.country?.code || catalogProvider?.country?.code || "";

    // Save to localStorage (replaces cookie storage)
    UtilityStorage.setUtility(
      providerHandle,
      partner.name,
      partner.region,
      normalizedPostalCode,
      partner.rebateAmount,
      programHandle,
      mysaOfferMode,
      providerCountry,
    );

    // Set cart attributes for rebate prequalification
    await this.#setCartAttributes(
      {
        ...partner,
        handle: providerHandle,
        programHandle,
        mysaOfferMode,
      },
      extraAttributes,
    );

    // Wait for cart update to propagate to Shopify's edge servers
    // This ensures section rendering API sees the updated cart attributes
    await this.#waitForCartPropagation();

    // Dispatch change event - listeners can now trust cart is ready
    document.dispatchEvent(
      new UtilityPartnerChangedEvent({
        ...this.#currentSelection,
        partner, // Include full partner data
      }),
    );

    // Log in preview mode
    if (window.Shopify?.preview) {
      log("Utility Partner Selected:", this.#currentSelection);
    }

    // Selection + storage succeeded. Cart attribute sync is best-effort and
    // does not gate this — analytics and UI treat the buyer as selected.
    return true;
  }

  /**
   * Wait for cart update to propagate to Shopify's servers
   * This ensures the Section Rendering API sees updated cart attributes
   * @param {number} [maxAttempts=5] - Maximum number of verification attempts
   * @param {number} [delayMs=500] - Delay between attempts
   */
  async #waitForCartPropagation(maxAttempts = 5, delayMs = 500) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      // Wait before checking
      await new Promise((resolve) => setTimeout(resolve, delayMs));

      try {
        const response = await fetch(
          `${Theme.routes.cart_url}.js?_=${Date.now()}`,
          {
            credentials: "same-origin",
            cache: "no-store",
          },
        );
        const cart = await response.json();

        if (cart.attributes?._utility_partner_code) {
          log(
            `Cart propagation confirmed (attempt ${attempt}):`,
            cart.attributes,
          );
          return; // Success
        }

        log(`Cart attribute not found (attempt ${attempt}/${maxAttempts})`);
      } catch (error) {
        log(`Cart verification failed (attempt ${attempt}):`, error);
      }
    }

    // Even after all attempts, continue - the cart update was sent
    log("Cart propagation not confirmed, proceeding anyway");
  }

  /**
   * Get current utility partner selection
   * @returns {UtilitySelection | null}
   */
  getSelection() {
    return this.#currentSelection;
  }

  /**
   * Check if user has selected a partner
   * @returns {boolean}
   */
  hasSelection() {
    return this.#currentSelection !== null;
  }

  /**
   * Clear utility partner selection
   */
  async clearSelection() {
    const previousSelection = this.#currentSelection;
    this.#currentSelection = null;

    // Clear from localStorage (keeps postal code)
    UtilityStorage.clearUtility();

    // Clear cart attributes
    await this.#clearCartAttributes();

    // Dispatch cleared event
    document.dispatchEvent(
      new UtilityPartnerClearedEvent({ previousSelection }),
    );

    if (window.Shopify?.preview) {
      log("Utility Partner Selection Cleared");
    }
  }

  /**
   * Clear cart attributes for rebate prequalification and provider lookup
   */
  async #clearCartAttributes() {
    try {
      // Use .js endpoint to get JSON response (cart_update_url returns HTML)
      const response = await fetch(`${Theme.routes.cart_url}/update.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          attributes: {
            _rebate_prequalify: "",
            _utility_partner_code: "",
            _utility_program_code: "",
            _utility_offer_mode: "",
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      const cart = await response.json();

      log("Cart attributes cleared");

      // Note: We don't dispatch cart:update here because we're only updating
      // cart attributes, not cart items. Components that need to know about
      // utility partner changes should listen to mysa:utility-partner-cleared event.

      return cart;
    } catch (error) {
      console.error("Failed to clear cart attributes:", error);
      reportError(error, { tags: { op: "clear-cart-attrs" } });
    }
  }

  /**
   * Update postal code for existing selection
   * @param {string} postalCode - New postal code
   * @param {string} [source] - Source of postal code update
   */
  updatePostalCode(postalCode, source = "user-confirmed") {
    const normalizedPostal = postalCode.replace(/\s/g, "").toUpperCase();

    // Update current selection if exists
    if (this.#currentSelection) {
      this.#currentSelection.postalCode = normalizedPostal;
    }

    // Update in localStorage
    const country = getCountryFromPostalCode(normalizedPostal);
    UtilityStorage.setPostalCode(normalizedPostal, source, country);

    document.dispatchEvent(
      new UtilityPostalUpdatedEvent({ postalCode: normalizedPostal }),
    );
  }

  /**
   * Get all available utility providers
   * @returns {Array<UtilityPartner>}
   */
  getAllProviders() {
    return this.#providers;
  }

  /**
   * Province/region from a catalog provider row (metaobject `province` or
   * stamped `region`). Used by enrichMatches and wizard analytics.
   * @param {{ region?: string, province?: string | { code?: string, iso?: string } } | null | undefined} provider
   * @returns {string}
   */
  static regionFromProvider(provider) {
    if (!provider) return "";
    if (provider.region) return provider.region;
    if (typeof provider.province === "string" && provider.province) {
      return provider.province;
    }
    return provider.province?.code || provider.province?.iso || "";
  }

  /**
   * Get a specific provider by code/handle
   * @param {string} code - Provider code or handle
   * @returns {UtilityPartner | null}
   */
  getProviderByCode(code) {
    if (!code) return null;
    const normalizedCode = canonicalUtilityHandle(code);

    return (
      this.#providers.find(
        (p) => p.code === normalizedCode || p.handle === normalizedCode,
      ) || null
    );
  }

  /**
   * Get a provider by name (case-insensitive)
   * @param {string} name - Provider name
   * @returns {UtilityPartner | null}
   */
  getProviderByName(name) {
    const normalizedName = name.toLowerCase().trim();
    return (
      this.#providers.find(
        (p) => p.name.toLowerCase().trim() === normalizedName,
      ) || null
    );
  }

  /**
   * Get provider data lookup maps for enriching API responses
   * Used internally by enrichMatches()
   * @returns {{ byCode: Record<string, UtilityPartner>, byHandle: Record<string, UtilityPartner>, byName: Record<string, UtilityPartner> }}
   */
  #getProviderMaps() {
    /** @type {Record<string, UtilityPartner>} */
    const byCode = {};
    /** @type {Record<string, UtilityPartner>} */
    const byHandle = {};
    /** @type {Record<string, UtilityPartner>} */
    const byName = {};

    for (const provider of this.#providers) {
      if (provider.code) byCode[provider.code] = provider;
      if (provider.handle) byHandle[provider.handle] = provider;
      if (provider.name) byName[provider.name] = provider;
    }

    return { byCode, byHandle, byName };
  }

  /**
   * Normalize API programs array to legacy { rebate, demandResponse } shape
   * @param {any} programs - API array or legacy nested object
   * @returns {{ rebate?: object, demandResponse?: object }}
   */
  #normalizeApiPrograms(programs) {
    if (!programs) return {};

    if (!Array.isArray(programs)) {
      return /** @type {{ rebate?: object, demandResponse?: object }} */ (
        programs
      );
    }

    /** @type {{ rebate?: object, demandResponse?: object }} */
    const normalized = {};

    for (const program of programs) {
      if (!program?.handle) continue;

      const collectionUrl = programCollectionPath(program.handle);
      const type = String(program.type || "").toLowerCase();

      if (type === "rebate" && !normalized.rebate) {
        normalized.rebate = {
          program_handle: program.handle,
          page_url: collectionUrl,
          validationType: program.validationType,
          validationCity: program.validationCity,
        };
      } else if (
        (type === "demand-response" || type === "demand_response") &&
        !normalized.demandResponse
      ) {
        normalized.demandResponse = {
          handle: program.handle,
          page_url: collectionUrl,
          validationType: program.validationType,
          validationCity: program.validationCity,
        };
      }
    }

    return normalized;
  }

  /**
   * Merge metaobject programs with API-normalized programs.
   * Metaobject data is preferred; API fills gaps when metaobject data is missing.
   * @param {{ rebate?: object, demandResponse?: object } | undefined} providerPrograms
   * @param {any} apiPrograms
   * @returns {{ rebate?: object, demandResponse?: object }}
   */
  #mergePrograms(providerPrograms, apiPrograms) {
    const fromProvider =
      providerPrograms && !Array.isArray(providerPrograms)
        ? providerPrograms
        : {};
    const fromApi = this.#normalizeApiPrograms(apiPrograms);

    return {
      rebate: fromProvider.rebate || fromApi.rebate,
      demandResponse: fromProvider.demandResponse || fromApi.demandResponse,
    };
  }

  /**
   * Resolve a program family from provider catalog by API handle and type.
   * @param {UtilityPartner | null | undefined} provider
   * @param {string} programHandle
   * @param {string} [programType]
   * @returns {{ handle: string, type: string, name: string, mysaOfferMode?: string, primaryIncentive?: PrimaryIncentive, widgetDescription?: string, popupCopy?: string, page_url?: string } | null}
   */
  resolveProgramFamily(provider, programHandle, programType) {
    if (!provider || !programHandle) return null;

    const catalog = provider.programsList;
    const needle = canonicalUtilityHandle(programHandle);
    if (Array.isArray(catalog) && catalog.length > 0) {
      const exact = catalog.find(
        (entry) => canonicalUtilityHandle(entry.handle) === needle,
      );
      if (exact) return exact;

      if (programType) {
        const normalizedType = programType.toLowerCase();
        const byType = catalog.filter(
          (entry) => entry.type?.toLowerCase() === normalizedType,
        );
        if (byType.length === 1) return byType[0];
      }

      return null;
    }

    const nested = provider.programs;
    if (!nested || Array.isArray(nested)) return null;

    if (canonicalUtilityHandle(nested.rebate?.program_handle) === needle) {
      return {
        handle: nested.rebate.program_handle,
        type: "rebate",
        name: nested.rebate.program_handle,
        mysaOfferMode: nested.rebate.mysaOfferMode || "",
        page_url: nested.rebate.page_url,
        popupCopy: provider.popupCopy,
        widgetDescription: provider.widgetDescription,
      };
    }

    if (canonicalUtilityHandle(nested.demandResponse?.handle) === needle) {
      return {
        handle: nested.demandResponse.handle,
        type: "demand-response",
        name:
          nested.demandResponse.program_name || nested.demandResponse.handle,
        page_url: nested.demandResponse.page_url,
        popupCopy: provider.popupCopy,
        widgetDescription: provider.widgetDescription,
      };
    }

    return null;
  }

  /**
   * Enrich API matches with full provider data from metaobjects
   * Centralizes the enrichment logic so consumers don't need to parse provider data
   * @param {Array<{utilityHandle: string, matchType?: string, programs?: any}>} matches - Raw API matches
   * @returns {Array<{utilityHandle: string, matchType?: string, programs?: any, code: string, name: string, logoSvg: string, handle: string}>}
   */
  enrichMatches(matches) {
    const maps = this.#getProviderMaps();

    return matches.map((match) => {
      const handle = match.utilityHandle;
      if (!handle) {
        return {
          ...match,
          code: "",
          name: "",
          logoSvg: "",
          handle: "",
          logoImg: "",
          popupCopy: "",
          widgetDescription: "",
          region: "",
        };
      }

      const lookup = canonicalUtilityHandle(handle);
      const provider =
        maps.byHandle[lookup] ||
        maps.byHandle[handle] ||
        maps.byCode[lookup] ||
        maps.byCode[handle] ||
        maps.byName[lookup] ||
        maps.byName[handle] ||
        null;

      return {
        ...match,
        code: provider?.code || handle,
        name:
          provider?.name ||
          handle.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        logoSvg: provider?.logoSvg || "",
        logoImg: provider?.logoImg || "",
        popupCopy: provider?.popupCopy || "",
        widgetDescription: provider?.widgetDescription || "",
        handle: provider?.handle || handle,
        programs: Array.isArray(match.programs)
          ? match.programs
          : this.#mergePrograms(provider?.programs, match.programs),
        // Stamp when catalog lookup hits (province.code / region on the row)
        region: UtilityPartnerManager.regionFromProvider(provider),
      };
    });
  }

  /**
   * Find providers by country and province
   * @param {string} country - Country code (e.g., 'CA', 'US')
   * @param {string | null} [province] - Province/state code (e.g., 'BC', 'ON')
   * @returns {Array<UtilityPartner>}
   */
  findProvidersByLocation(country, province = null) {
    return this.#providers.filter((provider) => {
      // Check country match
      const countryData = provider.country || {};
      const countryMatch =
        countryData.code === country || countryData.iso === country;

      if (!countryMatch) return false;

      // If province specified, check province match
      if (province) {
        const provinceData = provider.province || {};
        return provinceData.code === province || provinceData.iso === province;
      }

      return true;
    });
  }

  /**
   * Get the canonical provider handle for a provider display name.
   * In v2, `code` and `handle` both mirror the utility_provider metaobject handle.
   * @param {string} partnerName - Partner name
   * @returns {string | null} Provider handle or null if not found
   */
  getRebateHandle(partnerName) {
    const provider = this.#providers.find((p) => p.name === partnerName);
    return provider?.handle || provider?.code || null;
  }

  /**
   * Resolve the active selection's offer mode from storage or provider catalog.
   * @returns {string}
   */
  #getSelectionOfferMode() {
    if (!this.#currentSelection) return "";

    const stored = this.#currentSelection.offerMode;
    if (stored) return stored;

    const provider = this.getProviderByCode(this.#currentSelection.partnerCode);
    if (!provider) return "";

    const programHandle = this.#currentSelection.programHandle;
    if (programHandle) {
      const family = this.resolveProgramFamily(provider, programHandle);
      return family?.mysaOfferMode || "";
    }

    return provider.programs?.rebate?.mysaOfferMode || "";
  }

  /**
   * Whether the current utility selection should apply instant-checkout pricing.
   * Post-purchase and marketplace offers never change displayed price in the theme.
   * Ontario HRS is a hardcoded Liquid outlier and never uses `$app:offers` dollars.
   * Hilo still shows `$app:offers` checkout floors while the bonus is post-purchase.
   * @returns {boolean}
   */
  selectionAllowsInstantPricing() {
    if (!this.#currentSelection?.partnerCode) return false;
    return appliesOffersCheckoutFloors(
      this.#currentSelection.partnerCode,
      this.#getSelectionOfferMode(),
    );
  }

  /**
   * Get utility discount for a product handle
   * @param {string} productHandle - Product handle (e.g., 'mysa-baseboard' or 'mysa-baseboard-legacy')
   * @returns {number} Discount amount in cents, or 0 if no discount
   */
  getUtilityDiscount(productHandle) {
    if (!this.selectionAllowsInstantPricing()) {
      return 0;
    }

    if (!this.#currentSelection) {
      return 0;
    }

    const provider = this.getProviderByCode(this.#currentSelection.partnerCode);

    if (!provider) {
      return 0;
    }

    const { discountRules } = this.#getActiveDiscountContext(provider);

    if (!discountRules) {
      return 0;
    }

    const normalizedHandle = this.#normalizeHandle(productHandle);
    if (
      !this.#hasConfiguredUtilityIncentive(
        this.#currentSelection.partnerCode,
        normalizedHandle,
      )
    ) {
      return 0;
    }

    return discountRules[normalizedHandle] || 0;
  }

  /**
   * Get utility discount details for a product
   * @param {string} productHandle - Product handle (e.g., 'mysa-baseboard' or 'mysa-baseboard-legacy')
   * @param {number} quantity - Product quantity
   * @param {number} [basePrice] - Optional base price for calculating final price
   * @returns {{amount: number, totalAmount: number, label: string, providerName: string} | null}
   */
  getUtilityDiscountDetails(productHandle, quantity = 1, basePrice) {
    if (!this.selectionAllowsInstantPricing()) {
      return null;
    }

    if (!this.#currentSelection) {
      return null;
    }

    const provider = this.getProviderByCode(this.#currentSelection.partnerCode);

    if (!provider) {
      return null;
    }

    const { discountRules, discountPrograms } =
      this.#getActiveDiscountContext(provider);

    if (!discountRules) {
      log(
        `⚠️ getUtilityDiscountDetails: No discount rules for ${this.#currentSelection.partnerCode}`,
      );
      return null;
    }

    const normalizedHandle = this.#normalizeHandle(productHandle);
    if (
      !this.#hasConfiguredUtilityIncentive(
        this.#currentSelection.partnerCode,
        normalizedHandle,
      )
    ) {
      return null;
    }

    const discountPerUnit = discountRules[normalizedHandle];

    if (!discountPerUnit) {
      log(
        `⚠️ getUtilityDiscountDetails: No discount for ${productHandle} (normalized: ${normalizedHandle}) with provider ${provider.name}`,
        {
          availableProducts: Object.keys(discountRules),
        },
      );
      return null;
    }

    log(
      `✅ getUtilityDiscountDetails: Found discount for ${productHandle} (normalized: ${normalizedHandle}) with ${provider.name}:`,
      {
        discountPerUnit,
        quantity,
        totalAmount: discountPerUnit * quantity,
      },
    );

    return {
      amount: discountPerUnit,
      totalAmount: discountPerUnit * quantity,
      label: `${provider.name} incentive`,
      providerName: provider.name,
    };
  }

  /**
   * Get detailed program breakdown for a product (rebate + mysa discount separate)
   * Respects quantity limits for both rebates and Mysa discounts
   * @param {string} productHandle - Product handle (e.g., 'mysa-baseboard' or 'mysa-baseboard-legacy')
   * @param {number} quantity - Product quantity
   * @returns {{rebate: number, mysaDiscount: number, total: number, providerName: string, priceFloor: number|null, rebateQuantityLimit: number, mysaDiscountQuantityLimit: number, includeBYOB: boolean, includeGeneral: boolean, maxQuantity: number, programs: Array<{type: string, label: string, amount: number, perUnit: number, quantityApplied: number}>} | null}
   */
  getUtilityProgramBreakdown(productHandle, quantity = 1) {
    if (!this.selectionAllowsInstantPricing()) {
      return null;
    }

    if (!this.#currentSelection) {
      return null;
    }

    const provider = this.getProviderByCode(this.#currentSelection.partnerCode);
    const normalizedHandle = this.#normalizeHandle(productHandle);

    if (!provider) {
      return null;
    }

    if (
      !this.#hasConfiguredUtilityIncentive(
        this.#currentSelection.partnerCode,
        normalizedHandle,
      )
    ) {
      return null;
    }

    const { discountPrograms, rebateQuantityLimit: contextRebateLimit } =
      this.#getActiveDiscountContext(provider);

    if (!discountPrograms || !discountPrograms[normalizedHandle]) {
      return null;
    }

    const programs = discountPrograms[normalizedHandle];

    // Ensure rebateQuantityLimit is a number (handle string values or undefined)
    let rebateQuantityLimit = 0;
    if (typeof contextRebateLimit === "number") {
      rebateQuantityLimit = contextRebateLimit;
    } else if (contextRebateLimit != null) {
      rebateQuantityLimit = parseInt(String(contextRebateLimit), 10) || 0;
    } else if (typeof provider.rebateQuantityLimit === "number") {
      rebateQuantityLimit = provider.rebateQuantityLimit;
    } else if (provider.rebateQuantityLimit != null) {
      rebateQuantityLimit =
        parseInt(String(provider.rebateQuantityLimit), 10) || 0;
    }

    // Ensure mysaDiscountQuantityLimit is a number (handle string values or undefined)
    let mysaDiscountQuantityLimit = 0;
    if (typeof provider.mysaDiscountQuantityLimit === "number") {
      mysaDiscountQuantityLimit = provider.mysaDiscountQuantityLimit;
    } else if (provider.mysaDiscountQuantityLimit != null) {
      mysaDiscountQuantityLimit =
        parseInt(String(provider.mysaDiscountQuantityLimit), 10) || 0;
    }

    // Calculate quantities with limits applied
    // Rebate: only applies up to quantity limit (0 = no limit)
    const rebateQuantity =
      rebateQuantityLimit > 0
        ? Math.min(quantity, rebateQuantityLimit)
        : quantity;

    // Mysa discount: only applies up to quantity limit (0 = no limit)
    const mysaDiscountQuantity =
      mysaDiscountQuantityLimit > 0
        ? Math.min(quantity, mysaDiscountQuantityLimit)
        : quantity;

    // Build array of individual programs for display
    const programList = [];

    if (programs.rebate > 0) {
      programList.push({
        type: "rebate",
        label: `${provider.name} rebate`,
        amount: programs.rebate * rebateQuantity,
        perUnit: programs.rebate,
        quantityApplied: rebateQuantity,
      });
    }

    if (programs.mysaDiscount > 0) {
      programList.push({
        type: "mysa-discount",
        label: "Mysa discount",
        amount: programs.mysaDiscount * mysaDiscountQuantity,
        perUnit: programs.mysaDiscount,
        quantityApplied: mysaDiscountQuantity,
      });
    }

    // Calculate total with quantity limits applied
    const totalRebate = programs.rebate * rebateQuantity;
    const totalMysaDiscount = programs.mysaDiscount * mysaDiscountQuantity;
    const totalDiscount = totalRebate + totalMysaDiscount;

    return {
      rebate: totalRebate,
      mysaDiscount: totalMysaDiscount,
      total: totalDiscount,
      providerName: programs.providerName,
      priceFloor: programs.priceFloor, // null if no floor, cents if floor exists
      rebateQuantityLimit: rebateQuantityLimit,
      mysaDiscountQuantityLimit: mysaDiscountQuantityLimit,
      includeBYOB: programs.includeBYOB !== false, // Whether BYOB can stack with this utility
      includeGeneral: programs.includeGeneral !== false, // Whether general discount can stack
      maxQuantity: programs.maxQuantity || 0, // Max quantity for rebate (0 = no limit)
      programs: programList,
    };
  }

  /**
   * Check if a product is eligible for utility discount
   * @param {string} productHandle - Product handle (e.g., 'mysa-baseboard' or 'mysa-baseboard-legacy')
   * @returns {boolean}
   */
  isProductEligible(productHandle) {
    if (!this.selectionAllowsInstantPricing()) {
      return false;
    }

    if (!this.#currentSelection) {
      return false;
    }

    const provider = this.getProviderByCode(this.#currentSelection.partnerCode);

    if (!provider) {
      return false;
    }

    const { discountRules } = this.#getActiveDiscountContext(provider);

    if (!discountRules) {
      return false;
    }

    const normalizedHandle = this.#normalizeHandle(productHandle);
    if (
      !this.#hasConfiguredUtilityIncentive(
        this.#currentSelection.partnerCode,
        normalizedHandle,
      )
    ) {
      return false;
    }

    return normalizedHandle in discountRules;
  }

  /**
   * Set cart attributes for rebate prequalification and provider lookup
   * Preserves existing attributes and merges in any extra attributes passed
   * @param {UtilityPartner | { name: string, handle?: string, programHandle?: string }} partner - Partner or selection payload
   * @param {Object} [extraAttributes] - Optional extra attributes to include (e.g., { _should_receive_discount: "1" })
   */
  async #setCartAttributes(partner, extraAttributes = {}) {
    const provider =
      this.#providers.find((p) => p.name === partner.name) ||
      (partner.handle
        ? this.#providers.find(
            (p) => p.handle === partner.handle || p.code === partner.handle,
          )
        : null);

    if (!provider) {
      log(`No provider found for partner: ${partner.name}`);
      return;
    }

    const utilityPartnerHandle = provider.handle || provider.code;
    const utilityProgramHandle = partner.programHandle || "";
    const utilityOfferMode = partner.mysaOfferMode || "";

    try {
      // Fetch current cart to preserve existing attributes
      // Safari requires explicit credentials for consistent cookie handling
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        credentials: "same-origin",
      });

      if (!cartResponse.ok) {
        throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      }

      const currentCart = await cartResponse.json();
      const existingAttributes = currentCart.attributes || {};

      // Merge: existing -> extra -> core (core attributes take precedence)
      const mergedAttributes = {
        ...existingAttributes,
        ...extraAttributes,
        _utility_partner_code: utilityPartnerHandle,
        _utility_program_code: utilityProgramHandle,
        _utility_offer_mode: utilityOfferMode,
        _rebate_prequalify: "1",
      };

      // Use .js endpoint to get JSON response (cart_update_url returns HTML)
      const response = await fetch(`${Theme.routes.cart_url}/update.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          attributes: mergedAttributes,
        }),
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      const cart = await response.json();

      log("Cart attributes updated:", cart.attributes);

      // Note: We don't dispatch cart:update here because we're only updating
      // cart attributes, not cart items. Components that need to know about
      // utility partner changes should listen to mysa:utility-partner-changed event.

      return cart;
    } catch (error) {
      console.error("Failed to set cart attributes:", error);
      reportError(error, { tags: { op: "set-cart-attrs" } });
    }
  }

  /**
   * Get utility partner data from storage (static method)
   * @returns {UtilitySelection | null}
   * @deprecated Use getInstance().getSelection() instead
   */
  static getStoredSelection() {
    const data = UtilityStorage.get();

    if (!data?.utility) return null;

    return {
      partnerCode: data.utility.code,
      partnerHandle: data.utility.code,
      partnerName: data.utility.name,
      programHandle: data.utility.programHandle,
      offerMode: data.utility.offerMode || "",
      postalCode: data.postalCode?.value || "",
      region: data.utility.region,
      selectedAt: data.utility.selectedAt,
      rebateAmount: data.utility.rebateAmount || 0,
    };
  }

  /**
   * Clear all utility partner data (static method)
   */
  static clearStoredSelection() {
    UtilityStorage.clear();
  }
}

// Singleton instance
/** @type {UtilityPartnerManager | null} */
let instance = null;

/**
 * Get the singleton instance of UtilityPartnerManager
 * @returns {UtilityPartnerManager}
 */
export function getUtilityPartnerManager() {
  if (!instance) {
    instance = new UtilityPartnerManager();
  }
  return instance;
}

// Initialization is driven by `utility-bootstrap.js`, not an eager
// DOMContentLoaded auto-init: the manager's initialize() (parse 46 providers +
// build every discount rule) used to run on every page even though first paint
// is server-rendered ($app:offers + cart attributes). The bootstrap runs it
// immediately only for cohorts that need it on load (saved selection, ?utility=
// / ?sje= marketing links) and otherwise on the first user interaction, keeping
// that work off the critical path for the majority of visits that never engage.
// Other entry points (wizard connectedCallback, postal-code-checker submit) call
// the idempotent initialize() themselves when they need provider data.
